import {
  focusEditor,
  getPluginOptions,
  useComposedRef,
  useEditorRef,
  useHotkeys,
  useOnClickOutside,
} from '@udecode/plate-common';
import {
  getSelectionBoundingClientRect,
  UseVirtualFloatingOptions,
} from '@udecode/plate-floating';
import { LinkPlugin } from '@udecode/plate-link';
import { ChangeEventHandler, useCallback, useEffect } from 'react';
import { useFocused } from 'slate-react';

import { ELEMENT_LINK } from '../../../plateTypes';
import { triggerFloatingLinkInsert } from '../utils';
import {
  floatingLinkActions,
  floatingLinkSelectors,
  useFloatingLinkSelectors,
} from './floatingLinksStore';
import { useFloatingInterlinkEscape } from './useFloatingLinkEscape';
import { useVirtualFloatingLink } from './useVirtualFloatingLink';

export const useFloatingLinkInsertState = ({
  floatingOptions,
}: {
  floatingOptions?: UseVirtualFloatingOptions;
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
} = {}) => {
  const editor = useEditorRef();
  const focused = useFocused();
  const mode = useFloatingLinkSelectors().mode();
  const isOpen = useFloatingLinkSelectors().isOpen(editor.id);

  const { triggerFloatingLinkHotkeys } = getPluginOptions<LinkPlugin>(
    editor,
    ELEMENT_LINK,
  );

  useHotkeys(
    triggerFloatingLinkHotkeys!,
    (e) => {
      if (triggerFloatingLinkInsert(editor, { focused })) {
        e.preventDefault();
      }
    },
    {
      enableOnContentEditable: true,
    },
    [focused],
  );

  const ref = useOnClickOutside(
    () => {
      if (floatingLinkSelectors.mode() === 'insert') {
        floatingLinkActions.hide();
        focusEditor(editor, editor.selection!);
      }
    },
    {
      disabled: !isOpen,
    },
  );

  const floating = useVirtualFloatingLink({
    editorId: editor.id,
    open: isOpen && mode === 'insert',
    getBoundingClientRect: getSelectionBoundingClientRect,
    whileElementsMounted: () => {},
    ...floatingOptions,
  });
  const { update } = floating;

  // wait for update before focusing input
  useEffect(() => {
    if (isOpen) {
      update();
    }
  }, [isOpen, update]);

  useFloatingInterlinkEscape();

  return {
    floating,
    refClickOutside: ref,
  };
};

export const useFloatingLinkInsert = (
  state: ReturnType<typeof useFloatingLinkInsertState>,
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
) => {
  const onChange: ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
    floatingLinkActions.text(e.target.value);
  }, []);

  return {
    ref: useComposedRef<HTMLDivElement>(
      state.floating.refs.setFloating,
      state.refClickOutside,
    ),
    props: {
      style: {
        ...state.floating.style,
        zIndex: 1,
      },
    },
    textInputProps: {
      onChange,
      defaultValue: floatingLinkSelectors.text(),
    },
  };
};
